import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Security as SecurityIcon,
  Info as InfoIcon,
  PhotoCamera as PhotoCameraIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';
import Webcam from 'react-webcam';
import Tesseract from 'tesseract.js';

const SubmitLicense: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [requestData, setRequestData] = useState<any>(null);
  const [drivingLicenceNumber, setDrivingLicenceNumber] = useState('');
  const [success, setSuccess] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanStatus, setScanStatus] = useState<string | null>(null);
  const webcamRef = useRef<Webcam | null>(null);

  const steps = ['Verify Request', 'Enter Details', 'Complete'];

  const fetchRequestData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(
        `${API_BASE_URL}/dvla-license-requests/public/token/${token}`
      );
      setRequestData(response.data);
      setActiveStep(1);
    } catch (err: any) {
      console.error('Error fetching request:', err);
      setError(
        err.response?.data?.message ||
        err.response?.data?.error ||
        'Invalid or expired request'
      );
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchRequestData();
  }, [fetchRequestData]);

  const extractLicenseFromText = (text: string): string | null => {
    console.log('Raw OCR text:', text);
    
    // Common words/patterns to ignore (appear on UK driving licenses but aren't the number)
    const ignorePatterns = [
      'DRIVINGLICENCE', 'DRIVERLICENSE', 'LICENSE', 'LICENCE', 
      'UNITEDKINGDOM', 'GREATBRITAIN', 'SURNAME', 'FORENAME',
      'DATEOFBIRTH', 'PLACEOFBIRTH', 'ADDRESS', 'ISSUEDATE',
      'VALIDFROM', 'VALIDUNTIL', 'DVLA', 'SWANSEA', 'CATEGORIES',
      'RESTRICTIONS', 'ENDORSEMENTS', 'PHOTOCARD', 'SIGNATURE'
    ];
    
    // Split text into potential candidate strings
    const lines = text.split('\n').map(line => line.trim().toUpperCase());
    const allCandidates: string[] = [];
    
    // Look through each line
    for (const line of lines) {
      const lineClean = line.replace(/\s+/g, '');
      
      // Skip if line contains common license text
      if (ignorePatterns.some(pattern => lineClean.includes(pattern))) {
        continue;
      }
      
      // Extract potential license number patterns from this line
      // UK license format: 5 letters + 6 digits + 5 alphanumeric (usually 2 letters + 3 digits)
      
      // Strict pattern: exactly 5 letters, 6 digits, then 5 more characters
      const strictMatch = lineClean.match(/[A-Z]{5}[0-9]{6}[A-Z0-9]{5}/);
      if (strictMatch) {
        allCandidates.push(strictMatch[0]);
      }
      
      // Also look for 16-character alphanumeric strings that start with letters
      const alphanumericMatch = lineClean.match(/[A-Z]{2,}[0-9]{6}[A-Z0-9]{2,}/g);
      if (alphanumericMatch) {
        allCandidates.push(...alphanumericMatch.filter(m => m.length === 16));
      }
    }
    
    console.log('Candidates found:', allCandidates);
    
    // Score each candidate based on how well it matches UK license format
    const scoredCandidates = allCandidates.map(candidate => {
      let score = 0;
      
      // Must be exactly 16 characters
      if (candidate.length !== 16) return { candidate, score: -1 };
      
      // Check format: 5 letters + 6 digits + 5 alphanumeric
      const formatMatch = candidate.match(/^([A-Z]{5})([0-9]{6})([A-Z0-9]{5})$/);
      if (!formatMatch) return { candidate, score: 0 };
      
      const [, letters, digits, suffix] = formatMatch;
      
      // Bonus: First 5 chars are all letters (surname pattern)
      if (/^[A-Z]{5}$/.test(letters)) score += 10;
      
      // Bonus: Middle 6 are all digits (date encoding)
      if (/^[0-9]{6}$/.test(digits)) score += 10;
      
      // Check if the 6 digits could be a valid date encoding
      // Format: YYMMDD (year decade, month, day)
      const month = parseInt(digits.substring(1, 3));
      const day = parseInt(digits.substring(3, 5));
      
      // Month should be 01-12 or 51-62 (female encoding adds 50 to month)
      if ((month >= 1 && month <= 12) || (month >= 51 && month <= 62)) score += 5;
      
      // Day should be 01-31
      if (day >= 1 && day <= 31) score += 5;
      
      // Last 5 characters: typically 2 letters + 3 digits or similar
      const lastTwo = suffix.substring(0, 2);
      const lastThree = suffix.substring(2, 5);
      
      if (/^[A-Z]{2}$/.test(lastTwo)) score += 3;
      if (/^[0-9]{2}[A-Z0-9]$/.test(lastThree)) score += 3;
      
      console.log(`Scoring "${candidate}": ${score}`);
      return { candidate, score };
    });
    
    // Filter valid candidates (score > 0) and sort by score
    const validCandidates = scoredCandidates
      .filter(c => c.score > 0)
      .sort((a, b) => b.score - a.score);
    
    if (validCandidates.length > 0) {
      console.log('Best match:', validCandidates[0].candidate);
      return validCandidates[0].candidate;
    }
    
    // Fallback: if we found any 16-character alphanumeric string, return the first one
    const fallback = allCandidates.find(c => c.length === 16);
    if (fallback) {
      console.log('Using fallback:', fallback);
      return fallback;
    }
    
    console.log('No valid license number found');
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!drivingLicenceNumber) {
      setError('Please enter your driving license number');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await axios.post(
        `${API_BASE_URL}/dvla-license-requests/public/submit/${token}`,
        {
          drivingLicenceNumber: drivingLicenceNumber.toUpperCase().replace(/\s/g, '')
        }
      );

      setSuccess(true);
      setActiveStep(2);
    } catch (err: any) {
      console.error('Error submitting license:', err);
      setError(
        err.response?.data?.message ||
        err.response?.data?.error ||
        'Failed to submit license details'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleStartScan = async () => {
    if (!webcamRef.current) {
      setScanStatus('Camera not ready. Please allow access.');
      return;
    }

    try {
      setScanning(true);
      setScanStatus('Capturing image...');
      const screenshot = webcamRef.current.getScreenshot();

      if (!screenshot) {
        throw new Error('Failed to capture image');
      }

      setScanStatus('Reading license number...');
      
      // Use Tesseract with optimized settings for license cards
      const result = await Tesseract.recognize(screenshot, 'eng', {
        logger: (m: any) => {
          if (m.status === 'recognizing text') {
            setScanStatus(`Processing... ${Math.round(m.progress * 100)}%`);
          }
        }
      });
      
      const text = result?.data?.text || '';
      console.log('Full OCR text:', text);
      
      const extracted = extractLicenseFromText(text);

      if (extracted) {
        setDrivingLicenceNumber(formatLicenseNumber(extracted));
        setScanStatus('✓ License number detected!');
        setTimeout(() => {
          setScannerOpen(false);
          setScanStatus(null);
        }, 1000);
      } else {
        setScanStatus('Could not detect license number. Please ensure the license number (5D field) is clearly visible and try again, or enter it manually.');
      }
    } catch (scanErr: any) {
      console.error('Scan error:', scanErr);
      setScanStatus('Failed to scan. Please try again or enter manually.');
    } finally {
      setScanning(false);
    }
  };

  const formatLicenseNumber = (value: string) => {
    // Remove spaces and convert to uppercase
    const cleaned = value.toUpperCase().replace(/\s/g, '');
    // Format as XXXXX-XXXXXX-XX-XXX (UK license format)
    if (cleaned.length <= 5) return cleaned;
    if (cleaned.length <= 11) return `${cleaned.slice(0, 5)} ${cleaned.slice(5)}`;
    if (cleaned.length <= 13) return `${cleaned.slice(0, 5)} ${cleaned.slice(5, 11)} ${cleaned.slice(11)}`;
    return `${cleaned.slice(0, 5)} ${cleaned.slice(5, 11)} ${cleaned.slice(11, 13)} ${cleaned.slice(13, 16)}`;
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f5f5f5'
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#f5f5f5',
        py: 4
      }}
    >
      <Container maxWidth="md">
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
            Driving License Verification
          </Typography>
          <Typography variant="h6" color="text.secondary">
            SafetyCheckNow DVLA Integration
          </Typography>
        </Box>

        {/* Stepper */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Stepper activeStep={activeStep}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Paper>

        {/* Error Display */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Success View */}
        {success ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
            <Typography variant="h4" gutterBottom>
              Success!
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              Your driving license details have been successfully submitted and verified.
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              The DVLA check has been completed and {requestData?.businessName} has been notified.
            </Typography>
            <Alert severity="info" sx={{ mt: 3 }}>
              You can now close this window. Thank you for your cooperation.
            </Alert>
          </Paper>
        ) : requestData ? (
          <>
            {/* Business Info Card */}
            <Card sx={{ mb: 3, backgroundColor: '#e3f2fd' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <InfoIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6">Request Information</Typography>
                </Box>
                <Typography variant="body1" paragraph>
                  <strong>{requestData.businessName}</strong> has requested verification of your driving license.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Request sent to: {requestData.recipientEmail}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Expires: {new Date(requestData.expiresAt).toLocaleDateString('en-GB', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </Typography>
              </CardContent>
            </Card>

            {/* Security Info */}
            <Card sx={{ mb: 3, backgroundColor: '#f3e5f5' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <SecurityIcon sx={{ mr: 1, color: 'secondary.main' }} />
                  <Typography variant="h6">Your Privacy & Security</Typography>
                </Box>
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  <li>
                    <Typography variant="body2">
                      Only authorized personnel at {requestData.businessName} will have access
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2">
                      Your data is protected under UK data protection laws
                    </Typography>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Submission Form */}
            <Paper sx={{ p: 4 }}>
              <Typography variant="h5" gutterBottom>
                Enter Your Details
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Please provide your UK driving license number below.
              </Typography>

              <form onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  label="Driving License Number"
                  value={drivingLicenceNumber}
                  onChange={(e) => setDrivingLicenceNumber(formatLicenseNumber(e.target.value))}
                  placeholder="SMITH 701152 AB 9CD"
                  required
                  disabled={submitting}
                  sx={{ mb: 3 }}
                  helperText="Enter your full UK driving license number (16 characters)"
                  inputProps={{ maxLength: 19 }} // 16 chars + 3 spaces
                />

                <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 3 }}>
                  <Button
                    variant="outlined"
                    startIcon={<PhotoCameraIcon />}
                    onClick={() => {
                      setScanStatus(null);
                      setScannerOpen(true);
                    }}
                    disabled={submitting}
                  >
                    Scan license instead
                  </Button>
                </Box>

                <Alert severity="warning" sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    Important:
                  </Typography>
                  <Typography variant="body2">
                    By submitting this form, you consent to {requestData.businessName} performing a DVLA check on your driving license. 
                    The information retrieved will include your license status, endorsements, penalty points, and vehicle categories.
                  </Typography>
                </Alert>

                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  fullWidth
                  disabled={submitting}
                  startIcon={submitting ? <CircularProgress size={20} /> : null}
                >
                  {submitting ? 'Submitting...' : 'Submit & Verify License'}
                </Button>
              </form>
            </Paper>
          </>
        ) : null}

        {/* Scanner Dialog */}
        <Dialog
          open={scannerOpen}
          onClose={() => (!scanning ? setScannerOpen(false) : null)}
          fullScreen={isMobile}
          fullWidth={!isMobile}
          maxWidth={isMobile ? false : "md"}
          PaperProps={{
            sx: {
              ...(isMobile && {
                backgroundColor: '#000',
              })
            }
          }}
        >
          {isMobile && (
            <Box
              sx={{
                position: 'absolute',
                top: 16,
                right: 16,
                zIndex: 10,
              }}
            >
              <Button
                onClick={() => setScannerOpen(false)}
                disabled={scanning}
                sx={{
                  color: '#fff',
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  minWidth: 'auto',
                  p: 1,
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  }
                }}
              >
                <CloseIcon />
              </Button>
            </Box>
          )}
          
          {!isMobile && <DialogTitle>Scan Driving License</DialogTitle>}
          
          <DialogContent
            sx={{
              ...(isMobile && {
                p: 0,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                backgroundColor: '#000',
              })
            }}
          >
            {!isMobile && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Position your UK driving license horizontally in the frame. Ensure the license number (field 5d, usually at the top) is clearly visible and well-lit.
              </Typography>
            )}
            
            {isMobile && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 60,
                  left: 0,
                  right: 0,
                  px: 2,
                  zIndex: 10,
                }}
              >
                <Alert 
                  severity="info" 
                  sx={{ 
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    Hold your phone in landscape mode
                  </Typography>
                  <Typography variant="caption">
                    Ensure the 16-character license number (5d) is clearly visible in the frame
                  </Typography>
                </Alert>
              </Box>
            )}
            
            <Box
              sx={{
                position: 'relative',
                backgroundColor: '#000',
                borderRadius: isMobile ? 0 : 2,
                overflow: 'hidden',
                width: '100%',
                // Driving license aspect ratio (similar to credit card: 85.6mm x 54mm ≈ 16:10)
                aspectRatio: isMobile ? '16 / 9' : '16 / 10',
                mb: isMobile ? 0 : 2,
                ...(isMobile && {
                  height: '100vh',
                  aspectRatio: 'auto',
                })
              }}
            >
              <Webcam
                ref={webcamRef}
                audio={false}
                screenshotFormat="image/png"
                videoConstraints={{
                  facingMode: 'environment',
                  width: { ideal: 1920 },
                  height: { ideal: 1080 },
                  aspectRatio: { ideal: 16/9 }
                }}
                style={{ 
                  width: '100%', 
                  height: '100%',
                  objectFit: 'cover'
                }}
              />
              
              {/* Overlay guide frame for license positioning */}
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: isMobile ? '90%' : '80%',
                  // Maintain driving license aspect ratio for the guide
                  aspectRatio: '16 / 10',
                  border: '3px solid rgba(76, 175, 80, 0.8)',
                  borderRadius: 2,
                  boxShadow: '0 0 0 2000px rgba(0, 0, 0, 0.5)',
                  pointerEvents: 'none',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: -2,
                    left: -2,
                    right: -2,
                    bottom: -2,
                    border: '2px dashed rgba(255, 255, 255, 0.6)',
                    borderRadius: 2,
                  }
                }}
              >
                {/* Corner markers */}
                <Box sx={{ position: 'absolute', top: -2, left: -2, width: 20, height: 20, borderTop: '4px solid #4CAF50', borderLeft: '4px solid #4CAF50' }} />
                <Box sx={{ position: 'absolute', top: -2, right: -2, width: 20, height: 20, borderTop: '4px solid #4CAF50', borderRight: '4px solid #4CAF50' }} />
                <Box sx={{ position: 'absolute', bottom: -2, left: -2, width: 20, height: 20, borderBottom: '4px solid #4CAF50', borderLeft: '4px solid #4CAF50' }} />
                <Box sx={{ position: 'absolute', bottom: -2, right: -2, width: 20, height: 20, borderBottom: '4px solid #4CAF50', borderRight: '4px solid #4CAF50' }} />
                
                {/* License number location indicator */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: '15%',
                    left: '10%',
                    right: '10%',
                    height: '25%',
                    border: '2px solid rgba(255, 235, 59, 0.7)',
                    borderRadius: 1,
                    backgroundColor: 'rgba(255, 235, 59, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#fff',
                      backgroundColor: 'rgba(0, 0, 0, 0.7)',
                      px: 1,
                      py: 0.5,
                      borderRadius: 0.5,
                      fontSize: isMobile ? '0.65rem' : '0.75rem',
                      fontWeight: 'bold',
                    }}
                  >
                    License Number (5d)
                  </Typography>
                </Box>
              </Box>
              
              {isMobile && (
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 100,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    textAlign: 'center',
                    zIndex: 5,
                  }}
                >
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: '#fff',
                      backgroundColor: 'rgba(0, 0, 0, 0.7)',
                      px: 2,
                      py: 1,
                      borderRadius: 1,
                      display: 'inline-block'
                    }}
                  >
                    Align license within the green frame
                  </Typography>
                </Box>
              )}
            </Box>
            
            {scanStatus && (
              <Alert 
                severity={scanStatus.includes('detected') ? 'success' : 'info'} 
                sx={{ 
                  mb: isMobile ? 0 : 1,
                  ...(isMobile && {
                    position: 'absolute',
                    bottom: 80,
                    left: 16,
                    right: 16,
                    zIndex: 10,
                  })
                }}
              >
                {scanStatus}
              </Alert>
            )}
          </DialogContent>
          
          <DialogActions 
            sx={{ 
              px: 3, 
              pb: 2,
              ...(isMobile && {
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                justifyContent: 'center',
                gap: 2,
                py: 2,
              })
            }}
          >
            {!isMobile && (
              <Button onClick={() => setScannerOpen(false)} disabled={scanning}>
                Close
              </Button>
            )}
            <Button
              variant="contained"
              size={isMobile ? "large" : "medium"}
              startIcon={scanning ? <CircularProgress size={18} /> : <PhotoCameraIcon />}
              onClick={handleStartScan}
              disabled={scanning}
              sx={{
                ...(isMobile && {
                  minWidth: 200,
                  py: 1.5,
                })
              }}
            >
              {scanning ? 'Scanning...' : 'Capture & Scan'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Footer */}
        <Box sx={{ textAlign: 'center', mt: 4, color: 'text.secondary' }}>
          <Typography variant="body2">
            © {new Date().getFullYear()} SafetyCheckNow. All rights reserved.
          </Typography>
          <Typography variant="caption">
            Powered by Check-Now DVLA License Management System
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default SubmitLicense;


